import React from 'react';
import { Card, Col, Empty, Progress, Row, Statistic, Table, Tag, Typography } from 'antd';
import { Bar } from '@ant-design/plots';
import type { ColumnsType } from 'antd/es/table';

const { Paragraph, Text } = Typography;

interface ApiQsearchAnalysisProps {
  data: any;
  type: string;
}

const number = (value: unknown): number => Number(value || 0);
const formatNumber = (value: unknown): string => number(value).toLocaleString();

const SuccessRate: React.FC<{ value: unknown }> = ({ value }) => {
  const rate = number(value);
  return (
    <Progress
      percent={rate}
      size="small"
      status={rate < 80 ? 'exception' : 'normal'}
      format={() => `${rate.toFixed(1)}%`}
    />
  );
};

const ApiQsearchAnalysis: React.FC<ApiQsearchAnalysisProps> = ({ data, type }) => {
  const rows = Array.isArray(data?.data)
    ? data.data.map((item: any, index: number) => ({ ...item, key: `${type}-${index}` }))
    : [];

  if (!data || !Array.isArray(data.data)) {
    return <Empty description="無可用資料" />;
  }

  const timeRange = data.time_range ? (
    <Text type="secondary">
      查詢時間範圍：{data.time_range.start_date} ～ {data.time_range.end_date}
    </Text>
  ) : null;

  if (type === 'api_qsearch_overview') {
    const totalRequests = rows.reduce((sum: number, row: any) => sum + number(row.total_requests), 0);
    const successfulRequests = rows.reduce((sum: number, row: any) => sum + number(row.successful_requests), 0);
    const errorRequests = rows.reduce((sum: number, row: any) => sum + number(row.error_requests), 0);
    const successRate = totalRequests ? (successfulRequests / totalRequests) * 100 : 0;

    const apiKeyColumns: ColumnsType<any> = [
      {
        title: 'API key 代碼',
        dataIndex: 'api_key_code',
        fixed: 'left',
        width: 180,
        render: (value) => <Tag color="geekblue">{value}</Tag>,
      },
      { title: '成功請求', dataIndex: 'successful_requests', render: formatNumber },
      { title: '總請求', dataIndex: 'total_requests', render: formatNumber },
      {
        title: '成功率',
        dataIndex: 'success_rate',
        width: 150,
        render: (value) => <SuccessRate value={value} />,
      },
      { title: '403', dataIndex: 'forbidden_requests', render: formatNumber },
      { title: '429', dataIndex: 'rate_limited_requests', render: formatNumber },
      { title: '5xx', dataIndex: 'server_error_requests', render: formatNumber },
      { title: '活躍天數', dataIndex: 'active_days' },
      { title: '服務數', dataIndex: 'service_count' },
      { title: 'Endpoint 數', dataIndex: 'endpoint_count' },
      {
        title: '平均延遲',
        dataIndex: 'avg_latency',
        render: (value) => `${number(value).toFixed(3)} 秒`,
      },
      {
        title: 'P95 延遲',
        dataIndex: 'p95_latency',
        render: (value) => `${number(value).toFixed(3)} 秒`,
      },
      { title: '最後使用', dataIndex: 'last_usage_date', width: 120 },
    ];

    const columns: ColumnsType<any> = [
      { title: 'API 客戶', dataIndex: 'org_name', fixed: 'left', width: 210 },
      {
        title: 'API key 數',
        dataIndex: 'api_key_count',
        sorter: (a, b) => number(a.api_key_count) - number(b.api_key_count),
      },
      {
        title: '成功請求',
        dataIndex: 'successful_requests',
        sorter: (a, b) => number(a.successful_requests) - number(b.successful_requests),
        render: formatNumber,
      },
      {
        title: '總請求',
        dataIndex: 'total_requests',
        sorter: (a, b) => number(a.total_requests) - number(b.total_requests),
        render: formatNumber,
      },
      {
        title: '成功率',
        dataIndex: 'success_rate',
        width: 150,
        sorter: (a, b) => number(a.success_rate) - number(b.success_rate),
        render: (value) => <SuccessRate value={value} />,
      },
      { title: '403', dataIndex: 'forbidden_requests', render: formatNumber },
      { title: '429', dataIndex: 'rate_limited_requests', render: formatNumber },
      { title: '5xx', dataIndex: 'server_error_requests', render: formatNumber },
      { title: '活躍天數', dataIndex: 'active_days' },
      { title: '服務數', dataIndex: 'service_count' },
      { title: 'Endpoint 數', dataIndex: 'endpoint_count' },
      {
        title: '平均延遲',
        dataIndex: 'avg_latency',
        render: (value) => `${number(value).toFixed(3)} 秒`,
      },
      {
        title: 'P95 延遲',
        dataIndex: 'p95_latency',
        render: (value) => `${number(value).toFixed(3)} 秒`,
      },
      { title: '最後使用', dataIndex: 'last_usage_date', width: 120 },
    ];

    return (
      <>
        <Paragraph>
          外部 API 客戶獨立統計；點擊客戶列可查看各 API key 的使用概覽。API key 僅以不可逆雜湊代碼呈現。
        </Paragraph>
        {timeRange}
        <Row gutter={16} style={{ margin: '16px 0' }}>
          <Col xs={24} md={6}><Card><Statistic title="API 客戶" value={rows.length} /></Card></Col>
          <Col xs={24} md={6}><Card><Statistic title="成功請求" value={successfulRequests} /></Card></Col>
          <Col xs={24} md={6}><Card><Statistic title="錯誤請求" value={errorRequests} /></Card></Col>
          <Col xs={24} md={6}><Card><Statistic title="整體成功率" value={successRate} precision={1} suffix="%" /></Card></Col>
        </Row>
        <Card>
          <Table
            columns={columns}
            dataSource={rows}
            scroll={{ x: 1600 }}
            pagination={{ pageSize: 20 }}
            expandable={{
              rowExpandable: (row) => Array.isArray(row.api_keys) && row.api_keys.length > 0,
              expandedRowRender: (row) => (
                <Table
                  columns={apiKeyColumns}
                  dataSource={(row.api_keys || []).map((apiKey: any) => ({
                    ...apiKey,
                    key: `${row.key}-${apiKey.api_key_code}`,
                  }))}
                  pagination={false}
                  size="small"
                  scroll={{ x: 1450 }}
                />
              ),
            }}
          />
        </Card>
      </>
    );
  }

  if (type === 'api_qsearch_endpoints') {
    const columns: ColumnsType<any> = [
      { title: 'API 客戶', dataIndex: 'org_name', fixed: 'left', width: 200 },
      { title: '服務', dataIndex: 'service_name', width: 190 },
      { title: 'Endpoint', dataIndex: 'endpoint', width: 320 },
      { title: '總請求', dataIndex: 'total_requests', sorter: (a, b) => number(a.total_requests) - number(b.total_requests), render: formatNumber },
      { title: '成功請求', dataIndex: 'successful_requests', render: formatNumber },
      { title: '成功率', dataIndex: 'success_rate', width: 150, render: (value) => <SuccessRate value={value} /> },
      { title: '403', dataIndex: 'forbidden_requests', render: formatNumber },
      { title: '429', dataIndex: 'rate_limited_requests', render: formatNumber },
      { title: '5xx', dataIndex: 'server_error_requests', render: formatNumber },
      { title: '平均延遲', dataIndex: 'avg_latency', render: (value) => `${number(value).toFixed(3)} 秒` },
      { title: 'P95 延遲', dataIndex: 'p95_latency', render: (value) => `${number(value).toFixed(3)} 秒` },
    ];
    return (
      <Card>
        <Paragraph>依客戶、服務與 Endpoint 檢視 API 採用情況及錯誤類型。</Paragraph>
        {timeRange}
        <Table columns={columns} dataSource={rows} scroll={{ x: 1450 }} pagination={{ pageSize: 20 }} style={{ marginTop: 16 }} />
      </Card>
    );
  }

  if (type === 'api_qsearch_channels') {
    const columns: ColumnsType<any> = [
      { title: 'API 客戶', dataIndex: 'org_name', width: 220 },
      { title: 'Channel', dataIndex: 'channel', render: (value) => <Tag color="blue">{value}</Tag> },
      { title: '總請求', dataIndex: 'total_requests', sorter: (a, b) => number(a.total_requests) - number(b.total_requests), render: formatNumber },
      { title: '成功請求', dataIndex: 'successful_requests', render: formatNumber },
      { title: '錯誤請求', dataIndex: 'error_requests', render: formatNumber },
      { title: '成功率', dataIndex: 'success_rate', width: 180, render: (value) => <SuccessRate value={value} /> },
    ];
    return (
      <Card>
        <Paragraph>已將純文字與 JSON array 格式的 channel 正規化後統計。</Paragraph>
        {timeRange}
        <Table columns={columns} dataSource={rows} pagination={{ pageSize: 20 }} style={{ marginTop: 16 }} />
      </Card>
    );
  }

  if (type === 'api_qsearch_keywords') {
    const globalSummary = rows.find((row: any) => row.row_type === 'summary');
    const organizationRows = rows.filter((row: any) => row.row_type === 'organization');
    const keywordRows = rows.filter(
      (row: any) => row.row_type === (globalSummary ? 'global_keyword' : 'organization_keyword'),
    );

    const summary = globalSummary || {
      org_count: organizationRows.length,
      successful_requests: organizationRows.reduce(
        (sum: number, row: any) => sum + number(row.successful_requests),
        0,
      ),
      parseable_requests: organizationRows.reduce(
        (sum: number, row: any) => sum + number(row.parseable_requests),
        0,
      ),
      unparsed_requests: organizationRows.reduce(
        (sum: number, row: any) => sum + number(row.unparsed_requests),
        0,
      ),
      missing_query_requests: organizationRows.reduce(
        (sum: number, row: any) => sum + number(row.missing_query_requests),
        0,
      ),
      extracted_keyword_usages: organizationRows.reduce(
        (sum: number, row: any) => sum + number(row.extracted_keyword_usages),
        0,
      ),
    };
    const parseRate = number(summary.successful_requests) > 0
      ? (number(summary.parseable_requests) / number(summary.successful_requests)) * 100
      : 0;
    const chartData = keywordRows.slice(0, 10).map((row: any) => ({
      keyword: String(row.keyword || '').slice(0, 36),
      usage_count: number(row.usage_count),
    }));

    const organizationColumns: ColumnsType<any> = [
      { title: 'API 客戶', dataIndex: 'org_name', fixed: 'left', width: 220 },
      { title: '成功請求', dataIndex: 'successful_requests', render: formatNumber },
      {
        title: '可拆解率',
        width: 170,
        render: (_, row) => {
          const rate = number(row.successful_requests) > 0
            ? (number(row.parseable_requests) / number(row.successful_requests)) * 100
            : 0;
          return <Progress percent={rate} size="small" format={() => `${rate.toFixed(1)}%`} />;
        },
      },
      { title: '關鍵詞出現次數', dataIndex: 'extracted_keyword_usages', render: formatNumber },
      { title: '不重複關鍵詞', dataIndex: 'unique_keywords', render: formatNumber },
      { title: '複合／過長查詢', dataIndex: 'unparsed_requests', render: formatNumber },
      { title: '無 query', dataIndex: 'missing_query_requests', render: formatNumber },
      { title: '活躍天數', dataIndex: 'active_days' },
      { title: '最後使用', dataIndex: 'last_usage_date', width: 120 },
    ];
    const keywordColumns: ColumnsType<any> = [
      {
        title: '關鍵詞',
        dataIndex: 'keyword',
        width: 420,
        render: (value) => <Text ellipsis={{ tooltip: value }}>{value}</Text>,
      },
      { title: '出現次數', dataIndex: 'usage_count', sorter: (a, b) => number(a.usage_count) - number(b.usage_count), render: formatNumber },
      { title: '使用客戶', dataIndex: 'org_count' },
      { title: '活躍天數', dataIndex: 'active_days' },
      { title: '最後使用', dataIndex: 'last_usage_date', width: 120 },
    ];

    return (
      <>
        <Paragraph>
          僅統計 API QSearch 成功請求；JSON 陣列、JSON 字串與短單值會拆成關鍵詞，完整複合查詢不會顯示。
        </Paragraph>
        {timeRange}
        <Row gutter={[16, 16]} style={{ margin: '16px 0' }}>
          <Col xs={24} md={6}><Card><Statistic title="API 客戶" value={summary.org_count} /></Card></Col>
          <Col xs={24} md={6}><Card><Statistic title="成功請求" value={summary.successful_requests} /></Card></Col>
          <Col xs={24} md={6}><Card><Statistic title="可拆解請求" value={summary.parseable_requests} /></Card></Col>
          <Col xs={24} md={6}><Card><Statistic title="關鍵詞出現次數" value={summary.extracted_keyword_usages} /></Card></Col>
        </Row>
        <Card title={`關鍵詞拆解覆蓋率 ${parseRate.toFixed(1)}%`} style={{ marginBottom: 16 }}>
          <Progress percent={parseRate} status={parseRate < 50 ? 'exception' : 'normal'} />
          <Text type="secondary">
            複合／過長查詢 {formatNumber(summary.unparsed_requests)} 筆；無 query {formatNumber(summary.missing_query_requests)} 筆。
          </Text>
        </Card>
        {chartData.length > 0 && (
          <Card title="熱門關鍵詞 Top 10" style={{ marginBottom: 16 }}>
            <Bar data={chartData} xField="usage_count" yField="keyword" height={360} />
          </Card>
        )}
        <Card title="API 客戶關鍵詞使用" style={{ marginBottom: 16 }}>
          <Table columns={organizationColumns} dataSource={organizationRows} scroll={{ x: 1350 }} pagination={{ pageSize: 20 }} />
        </Card>
        <Card title={globalSummary ? '整體熱門關鍵詞' : '所選客戶熱門關鍵詞'}>
          <Table columns={keywordColumns} dataSource={keywordRows} scroll={{ x: 900 }} pagination={{ pageSize: 20 }} />
        </Card>
      </>
    );
  }

  return <Empty description="未知的 API QSearch 分析類型" />;
};

export default ApiQsearchAnalysis;
